import {
  createLegacySessionAdapter,
  createMockAuthAdapter,
  type AuthAdapter,
  type RuntimeConfig,
} from '@eis/core';
import { personas, DEFAULT_PERSONA, type PersonaId } from '@eis/testing';

/**
 * The auth adapter this environment's `config.json` asks for, built once at bootstrap.
 *
 * Async because the OIDC adapter is a LAZY chunk (`@eis/core/auth/oidc-session`, SDD-019): it
 * carries the session API of authservice, and every deployed console today runs
 * `legacy-session` (OQ-41), so the shell everybody downloads does not carry it.
 */
export async function loadAuthAdapter(config: RuntimeConfig): Promise<AuthAdapter> {
  if (config.auth.provider === 'legacy-session') {
    return createLegacySessionAdapter({
      legacyOrigin: config.auth.legacyOrigin ?? '',
      legacyApiBaseUrl: config.auth.legacyApiBaseUrl ?? '',
      suiteUrl: config.chrome.suiteUrl,
    });
  }
  if (config.auth.provider === 'oidc') {
    const { createOidcAuthAdapter } = await import('@eis/core/auth/oidc-session');
    return createOidcAuthAdapter({
      issuer: config.auth.issuer ?? '',
      clientId: config.auth.clientId ?? '',
      // The console is served from one origin per environment, so the default is the only value
      // that is ever right; naming it explicitly is for the case where it is not.
      redirectUri: config.auth.redirectUri ?? globalThis.location.origin,
      scopes: config.auth.scopes,
      // authservice, the token handler (T-42), is mounted under the same API base as every other
      // service: `/api/auth` behind the reverse proxy. Same origin, so the refresh cookie is a
      // first-party cookie of this console.
      sessionBaseUrl: absoluteApiUrl(config.apiBaseUrl, '/auth'),
    });
  }
  const persona = config.auth.defaultPersona as PersonaId;
  return createMockAuthAdapter(personas, persona in personas ? persona : DEFAULT_PERSONA);
}

/** `apiBaseUrl` is usually origin-relative (`/api`); the SDK client wants an absolute base. */
function absoluteApiUrl(apiBaseUrl: string, path: string): string {
  const trimmed = apiBaseUrl.replace(/\/+$/, '');
  const absolute = /^[a-z][a-z\d+\-.]*:/i.test(trimmed)
    ? trimmed
    : `${globalThis.location.origin}${trimmed}`;
  return `${absolute}${path}`;
}
